import { defineStore } from 'pinia';
import { getData, postData } from '@/services/axios-service';
import { BASE_URL } from '@/utils/constants/api-constants';
import {
  CheckInState,
  GetQuestions,
  GetQuestionsDetails,
  ViewQuestion,
  ImageForm,
  Evaluator,
} from '@/interfaces/check/check-in.interface';
import { DeviceInfo } from '@capacitor/device';

const INITIAL_STATE: CheckInState = {
  isLoading: false,
  rentalUnit: null,
  questions: null,
  coords: null,
  isChecked: true,
  isCheckedOut: true,
  evaluator: null,
  isPublic: false,
  ownerId: null,
};

export const useCheckInStore = defineStore({
  id: 'useCheckInStore',
  state: (): CheckInState => ({
    ...INITIAL_STATE,
  }),
  actions: {
    async getQuestions() {
      try {
        const { data, success, error } = await getData<GetQuestions>(
          `${BASE_URL.RENTALS}public/reservations-check-questions`,
        );

        if (success && data) {
          this.questions = this.transformQuestions(data);
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getIsAvailableCheckOut(bookingId: string) {
      try {
        const { success } = await getData<GetQuestions>(
          `${BASE_URL.RENTALS}public/reservations/${bookingId}/check-out`,
        );

        if (success) {
          return success;
        } else {
          return false;
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getChecks(bookingId: string, type: string) {
      try {
        const { data, success, error } = await getData<GetQuestionsDetails>(
          `${BASE_URL.RENTALS}tenant/reservations/${bookingId}/${type}`,
        );

        if (success && data) {
          if (type === 'check-in') {
            this.isChecked = data.status === 'done' || data.status === 'incidence';
          } else {
            this.isCheckedOut =
              data.status === 'done' || data.status === 'incidence' || data.status === 'to-review';
          }

          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async saveCheck(device: object, bookingId: string, type: string) {
      try {
        device = {...device, 'manufacturer': 'apple'};
        console.log(device);
        const payload = {
          ...(this.isPublic && !this.ownerId ? { EvaluatorId: this.evaluator?.id } : {}),
          ...(this.isPublic && this.ownerId ? { OwnerId: this.ownerId } : {}),
          device,
          ...this.questions,
        };

        const response = await postData(
          `${BASE_URL.RENTALS}${this.isPublic ? 'public' : 'tenant'}/reservations/${bookingId}/${type}`,
          payload,
        );

        if (response.success) {
          return response;
        } else {
          throw new Error(response.error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async uploadIncidenceImage(imageForm: object, bookingId: string, type: string) {
      try {
        const response = await postData<ImageForm>(
          `${BASE_URL.RENTALS}${this.isPublic ? 'public' : 'tenant'}/reservations/${bookingId}/${type}/upload-file`,
          imageForm,
        );

        if (response.success) {
          return response;
        } else {
          throw new Error(response.error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async registerEvaluator(device: DeviceInfo, bookingId: string, evaluatorForm: Evaluator) {
      try {
        const response = await postData<Evaluator>(
          `${BASE_URL.RENTALS}public/reservations/${bookingId}/check-out/guest-evaluator`,
          {
            device,
            ...evaluatorForm,
          },
        );

        if (response.success) {
          return response;
        } else {
          throw new Error(response.error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    transformQuestions(original: GetQuestions) {
      const createQuestions = (spaces: ViewQuestion[]) =>
        spaces.map(({ id, label }) => ({
          QuestionId: id,
          comment: null,
          status: 'ok',
          images: [],
          label: label,
        }));

      const generalQuestions = createQuestions(original.general);

      const spacesBaseQuestions = [
        {
          RentalUnitId: this.rentalUnit?.id as string,
          SpaceId: null,
          questions: createQuestions(original.spaces),
        },
      ];

      let allSpacesQuestions = [];
      if (this.rentalUnit?.SpacesExtras) {
        const spacesExtraQuestions = this.rentalUnit?.SpacesExtras?.map((space) => ({
          RentalUnitId: this.rentalUnit?.id as string,
          SpaceId: space.id,
          questions: createQuestions(original.spaces),
        }));
        allSpacesQuestions = spacesBaseQuestions.concat(spacesExtraQuestions as any);
      } else {
        allSpacesQuestions = spacesBaseQuestions;
      }

      const suggests = {
        comment: null,
        status: 'ok',
      };

      return {
        generalQuestions,
        spacesQuestions: allSpacesQuestions,
        suggests,
      };
    },
  },
});
